import React from 'react';
import { motion } from 'motion/react';
import type { BookingCategoryClass } from '@/features/booking/types/bookingTypes';
import { getTicketCategoryStyle, isPremiumCategory } from '@/features/booking/components/TicketCardStyles';
import { getTimezoneAbbreviation } from '@/lib/utils';

export interface MobileTierRibbonProps {
  /** Booking category class for tier styling */
  tierClass?: BookingCategoryClass;
  /** Event start time ISO string */
  startTime?: string;
  /** Timezone ID for formatting */
  timezoneId?: string;
  /** Optional className */
  className?: string;
}

/**
 * Mobile-specific tier ribbon displaying date/time on the left and tier on the right.
 * Designed to be sticky under the mobile hero header.
 */
export const MobileTierRibbon: React.FC<MobileTierRibbonProps> = ({
  tierClass = 'Standard',
  startTime,
  timezoneId,
  className = '',
}) => {
  const style = getTicketCategoryStyle(tierClass);
  const isPremium = isPremiumCategory(tierClass);

  // Format date and time onto two lines
  const formatDateInfo = () => {
    if (!startTime || !timezoneId) return null;
    const date = new Date(startTime);
    const tzAbbr = getTimezoneAbbreviation(startTime, timezoneId);
    
    // e.g., "TUE, May 12, 2026"
    const day = date.toLocaleDateString('en-US', { weekday: 'short', timeZone: timezoneId }).toUpperCase();
    const dateStr = date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', timeZone: timezoneId });
    
    // e.g., "8:00 PM BST"
    const timeStr = `${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: timezoneId })} ${tzAbbr}`;

    return {
      line1: `${day}, ${dateStr}`,
      line2: timeStr,
    };
  };

  const dateInfo = formatDateInfo();
  const tierName = tierClass === 'Standard' ? '' : tierClass.toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`relative w-full overflow-hidden flex items-center justify-between px-5 py-2 shadow-lg backdrop-blur-md ${className}`}
      style={{
        background: `linear-gradient(90deg, ${style.borderColor}20, ${style.borderColor}40, ${style.borderColor}20)`,
        backgroundColor: 'rgba(10,10,10,0.85)', // Ensures text readability on scroll
        borderTop: `1px solid ${style.borderColor}60`,
        borderBottom: `1px solid ${style.borderColor}60`,
      }}
    >
      {/* Left Column: Date & Time */}
      <div className="flex flex-col">
        {dateInfo ? (
          <>
            <span className="text-[13px] font-bold tracking-wide text-white drop-shadow-sm leading-tight">
              {dateInfo.line1}
            </span>
            <span className="text-[11px] font-bold mt-0.5 tracking-wide uppercase drop-shadow-sm leading-tight" style={{ color: style.secondaryTextColor || '#a1a1aa' }}>
              {dateInfo.line2}
            </span>
          </>
        ) : (
          <span className="text-[13px] font-bold text-gray-500">TBD</span>
        )}
      </div>

      {/* Right Column: Tier Info */}
      {isPremium && tierName && (
        <div className="flex flex-col items-end justify-center">
          <span className="text-xs font-extrabold tracking-widest uppercase drop-shadow-md leading-none" style={{ color: style.accentColor }}>
            {tierName}
          </span>
          <span className="text-[9px] font-bold tracking-[0.2em] uppercase mt-1 leading-none" style={{ color: style.accentColor, opacity: 0.8 }}>
            TIER
          </span>
        </div>
      )}

      {/* Shimmer overlay (only for premium tiers) */}
      {isPremium && (
        <motion.div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute inset-0 h-full"
            style={{
              background: `linear-gradient(90deg, transparent 0%, ${style.accentColor}30 50%, transparent 100%)`,
              width: '150%',
            }}
            animate={{ x: ['-100%', '100%'] }}
            transition={{ duration: 3.5, ease: 'linear', repeat: Infinity }}
          />
        </motion.div>
      )}
    </motion.div>
  );
};
